import { BehaviorSubject, Observable, take } from "rxjs";

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export class Paginator<T> {
  private _results = new BehaviorSubject<T[]>([]);
  private _loading = new BehaviorSubject<boolean>(false);
  private _error = new BehaviorSubject<any>(null);
  private _nextUrl: string | null = null;
  private _previousUrl: string | null = null;
  count = 0;

  results$ = this._results.asObservable();
  loading$ = this._loading.asObservable();
  error$ = this._error.asObservable();

  get hasNext() { return !!this._nextUrl; }
  get hasPrevious() { return !!this._previousUrl; }

  constructor(
    private initial$: Observable<PaginatedResponse<T>>,
    private fetchPage: (url: string) => Observable<PaginatedResponse<T>>
  ) {}

  load() {
    this._error.next(null);
    this._loading.next(true);
    this.initial$.pipe(take(1)).subscribe({
      next: (res) => this._consume(res),
      error: (err) => this._handleError(err),
    });
  }

  loadNext() {
    if (!this._nextUrl || this._loading.getValue()) return;
    this._error.next(null);
    this._loading.next(true);
    this.fetchPage(this._nextUrl).pipe(take(1)).subscribe({
      next: (res) => this._consume(res),
      error: (err) => this._handleError(err),
    });
  }

  loadPrevious() {
    if (!this._previousUrl || this._loading.getValue()) return;
    this._error.next(null);
    this._loading.next(true);
    this.fetchPage(this._previousUrl).pipe(take(1)).subscribe({
      next: (res) => this._consume(res),
      error: (err) => this._handleError(err),
    });
  }

  private _consume(res: PaginatedResponse<T>) {
    this.count = res.count;
    this._nextUrl = res.next;
    this._previousUrl = res.previous;
    this._results.next(res.results);
    this._loading.next(false);
    this._error.next(null);
  }

  private _handleError(err: any) {
    this._loading.next(false);
    this._error.next(err);
  }

  // only for cleaning up resources menually sort of scenario. Not really needed but might be in some case perhaps
  destroy() {
    this._results.complete();
    this._loading.complete();
    this._error.complete();
  }
}